import React, { useState, useEffect, useRef } from 'react';
import { BlobServiceClient, ContainerClient } from '@azure/storage-blob';
import SessionView from './SessionView';
import './FileList.css';

interface FileInfo {
    displayName: string;
    fullName: string;
    timestamp: string;
    isFinished: boolean;
    sessionId?: string; // Add optional sessionId field
}

interface DayGroup {
    date: string;
    inProgress: FileInfo[];
    finished: FileInfo[];
}

interface RoomFiles {
    [date: string]: DayGroup;
}

interface GroupedFiles {
    [room: string]: RoomFiles;
}

interface SelectionState {
    rooms: { [key: string]: boolean };
    dates: { [key: string]: boolean };
    sessions: { [key: string]: boolean };
}

const FileList: React.FC = () => {
    const [files, setFiles] = useState<GroupedFiles>({});
    const [selectedSession, setSelectedSession] = useState<{ content: string; id: string } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selection, setSelection] = useState<SelectionState>({
        rooms: {},
        dates: {},
        sessions: {}
    });
    const containerClientRef = useRef<ContainerClient | null>(null);

    const formatTimestamp = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-GB', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        });
    };

    useEffect(() => {
        const fetchFiles = async () => {
            const cookies = document.cookie.split('; ');
            const getCookieValue = (name: string) =>
                cookies.find((row) => row.startsWith(`${name}=`))?.split('=')[1] || '';

            const storageAccountName = getCookieValue('storageAccountName');
            const containerName = getCookieValue('containerName');

            if (!storageAccountName || !containerName) {
                setError('Azure settings are incomplete. Please set them up first.');
                return;
            }

            try {
                const blobServiceClient = new BlobServiceClient(
                    `https://${storageAccountName}.blob.core.windows.net`
                );
                containerClientRef.current = blobServiceClient.getContainerClient(containerName);
                const groupedFiles: GroupedFiles = {};

                for await (const blob of containerClientRef.current.listBlobsFlat()) {
                    const fileName = blob.name;
                    const isFinished = fileName.toLowerCase().startsWith('finished/');
                    const cleanedName = fileName.replace(/^finished\//i, '');
                    const roomName = cleanedName.split('-')[0];
                    const lastModified = blob.properties.lastModified?.toISOString() || 'Unknown';
                    const dateKey = new Date(lastModified).toLocaleDateString();
                    const sessionId = cleanedName.split('-')[1]; // Extract session ID

                    if (!groupedFiles[roomName]) {
                        groupedFiles[roomName] = {};
                    }
                    if (!groupedFiles[roomName][dateKey]) {
                        groupedFiles[roomName][dateKey] = {
                            date: dateKey,
                            inProgress: [],
                            finished: []
                        };
                    }

                    const fileInfo: FileInfo = {
                        displayName: formatTimestamp(lastModified), // Just show the time instead of the full filename
                        fullName: fileName,
                        sessionId: sessionId, // Add session ID to FileInfo
                        timestamp: lastModified,
                        isFinished
                    };

                    if (isFinished) {
                        groupedFiles[roomName][dateKey].finished.push(fileInfo);
                    } else {
                        groupedFiles[roomName][dateKey].inProgress.push(fileInfo);
                    }
                }

                // Filter out in-progress items that exist in finished
                Object.keys(groupedFiles).forEach(room => {
                    Object.keys(groupedFiles[room]).forEach(date => {
                        const finishedSessionIds = new Set(
                            groupedFiles[room][date].finished.map(f => f.sessionId)
                        );
                        groupedFiles[room][date].inProgress = groupedFiles[room][date].inProgress
                            .filter(file => !finishedSessionIds.has(file.sessionId));
                    });
                });

                setFiles(groupedFiles);
            } catch (err) {
                console.error('Error fetching files:', err);
                setError('Failed to fetch files.');
            }
        };

        fetchFiles();
    }, []);

    const blobToString = async (blob: Blob): Promise<string> => {
        const arrayBuffer = await blob.arrayBuffer();
        const decoder = new TextDecoder('utf-8');
        return decoder.decode(arrayBuffer);
    };

    const handleSessionSelect = (sessionId: string) => {
        setSelection(prev => ({
            ...prev,
            sessions: {
                ...prev.sessions,
                [sessionId]: !prev.sessions[sessionId]
            }
        }));
    };

    const handleCardClick = (sessionId: string) => {
        handleSessionSelect(sessionId);
    };

    const handleRoomSelect = (roomName: string, checked: boolean) => {
        const newSelection = { ...selection };
        newSelection.rooms[roomName] = checked;
        
        // Update all dates and sessions under this room
        Object.keys(files[roomName] || {}).forEach(date => {
            newSelection.dates[`${roomName}-${date}`] = checked;
            files[roomName][date].inProgress.forEach(file => {
                newSelection.sessions[file.fullName] = checked;
            });
            files[roomName][date].finished.forEach(file => {
                newSelection.sessions[file.fullName] = checked;
            });
        });

        setSelection(newSelection);
    };

    const handleDateSelect = (roomName: string, date: string, checked: boolean) => {
        const newSelection = { ...selection };
        newSelection.dates[`${roomName}-${date}`] = checked;
        
        // Update all sessions under this date
        files[roomName][date].inProgress.forEach(file => {
            newSelection.sessions[file.fullName] = checked;
        });
        files[roomName][date].finished.forEach(file => {
            newSelection.sessions[file.fullName] = checked;
        });

        setSelection(newSelection);
    };

    const handleSessionSelectCheckbox = (sessionId: string, checked: boolean) => {
        setSelection(prev => ({
            ...prev,
            sessions: { ...prev.sessions, [sessionId]: checked }
        }));
    };

    const handleSelectAll = (checked: boolean) => {
        const newSelection = { ...selection };
        
        // Update all rooms
        Object.keys(files).forEach(roomName => {
            newSelection.rooms[roomName] = checked;
            
            // Update all dates in this room
            Object.keys(files[roomName]).forEach(date => {
                newSelection.dates[`${roomName}-${date}`] = checked;
                
                // Update all sessions in this date
                files[roomName][date].inProgress.forEach(file => {
                    newSelection.sessions[file.fullName] = checked;
                });
                files[roomName][date].finished.forEach(file => {
                    newSelection.sessions[file.fullName] = checked;
                });
            });
        });

        setSelection(newSelection);
    };

    const isAllSelected = () => {
        return Object.keys(files).every(roomName =>
            selection.rooms[roomName] || false
        );
    };

    const getSelectedSessions = () => {
        return Object.entries(selection.sessions)
            .filter(([_, isSelected]) => isSelected)
            .map(([sessionId]) => sessionId);
    };

    const getDateState = (roomName: string, date: string) => {
        const dateFiles = [...files[roomName][date].inProgress, ...files[roomName][date].finished];
        const selectedCount = dateFiles.filter(file => selection.sessions[file.fullName]).length;
        
        if (selectedCount === 0) return 'unchecked';
        if (selectedCount === dateFiles.length) return 'checked';
        return 'partial';
    };

    const getRoomState = (roomName: string) => {
        const dates = Object.keys(files[roomName]);
        const dateStates = dates.map(date => getDateState(roomName, date));
        
        if (dateStates.every(state => state === 'checked')) return 'checked';
        if (dateStates.every(state => state === 'unchecked')) return 'unchecked';
        return 'partial';
    };

    const getAllSelectionState = () => {
        const allFiles = Object.keys(files).reduce<Array<{ fullName: string }>>(
            (acc, roomName: string) => {
                Object.keys(files[roomName]).forEach((date: string) => {
                    acc.push(...files[roomName][date].inProgress);
                    acc.push(...files[roomName][date].finished);
                });
                return acc;
            },
            []
        );
        
        const selectedCount = allFiles.filter(
            (file: { fullName: string }) => selection.sessions[file.fullName]
        ).length;
        
        if (selectedCount === 0) return 'unchecked';
        if (selectedCount === allFiles.length) return 'checked';
        return 'partial';
    };

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div className="container">
            <div className="main-content">
                <div className="file-list">
                    <div className="select-all-header">
                        <label className="custom-checkbox">
                            <input
                                type="checkbox"
                                checked={getAllSelectionState() === 'checked'}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                            <span className={`checkmark ${getAllSelectionState()}`}></span>
                        </label>
                        <span className="select-all-text">Select All</span>
                    </div>
                    {Object.entries(files).map(([roomName, dates]) => (
                        <div key={roomName} className="room-section">
                            <div className="room-header">
                                <label className="custom-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={getRoomState(roomName) === 'checked'}
                                        onChange={(e) => handleRoomSelect(roomName, e.target.checked)}
                                    />
                                    <span className={`checkmark ${getRoomState(roomName)}`}></span>
                                </label>
                                <h2 className="room-title">{roomName}</h2>
                            </div>
                            {Object.entries(dates).map(([date, sessions]) => (
                                <div key={date} className="day-section">
                                    <div className="date-header">
                                        <label className="custom-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={getDateState(roomName, date) === 'checked'}
                                                onChange={(e) => handleDateSelect(roomName, date, e.target.checked)}
                                            />
                                            <span className={`checkmark ${getDateState(roomName, date)}`}></span>
                                        </label>
                                        <h3 className="day-title">{date}</h3>
                                    </div>
                                    <div className="status-grid">
                                        <div className="status-column">
                                            <h4 className="status-title">In Progress</h4>
                                            {sessions.inProgress.map((file, index) => (
                                                <div key={index} 
                                                     className="file-card"
                                                     onClick={() => handleCardClick(file.fullName)}>
                                                    <label className="custom-checkbox" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selection.sessions[file.fullName] || false}
                                                            onChange={() => handleSessionSelect(file.fullName)}
                                                        />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                    <div className="file-name">{file.displayName}</div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="status-column">
                                            <h4 className="status-title">Finished</h4>
                                            {sessions.finished.map((file, index) => (
                                                <div key={index} 
                                                     className="file-card"
                                                     onClick={() => handleCardClick(file.fullName)}>
                                                    <label className="custom-checkbox" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selection.sessions[file.fullName] || false}
                                                            onChange={() => handleSessionSelect(file.fullName)}
                                                        />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                    <div className="file-name">{file.displayName}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <SessionView 
                    selectedSessions={getSelectedSessions()}
                />
            </div>
        </div>
    );
};

export default FileList;
